import { useEffect, useState, type FormEvent } from 'react';
import { ArrowLeft, ArrowUpRight, CheckCheck, Link2, RefreshCw, Video } from 'lucide-react';
import type {
  SocialMedia,
  LinkedMedia,
  MediaInsightSnapshot,
  PublicationReview,
  State,
} from '../shared/model';

const stamp = (date: string) =>
  new Date(date).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
const age = (seconds: number) =>
  seconds < 86400
    ? `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}min`
    : `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h`;
const format = (m: SocialMedia) =>
  m.productType === 'STORY'
    ? 'Stories'
    : m.productType === 'REELS'
      ? 'Reel'
      : m.mediaType === 'CAROUSEL_ALBUM'
        ? 'Carrossel'
        : 'Post';
const accountLabel = (m: SocialMedia) =>
  m.platform === 'facebook' ? m.username : `@${m.username}`;
const channelLabel = (m: SocialMedia) => (m.platform === 'facebook' ? 'Facebook' : 'Instagram');
const metricValue = (snapshot: MediaInsightSnapshot | undefined, keys: string[]) => {
  const metric = snapshot?.metrics.find((m) => keys.includes(m.key));
  return metric?.status === 'available' && metric.value !== null
    ? metric.value.toLocaleString('pt-BR')
    : '—';
};
async function request(path: string, csrf?: string, body?: unknown) {
  const response = await fetch(
    path,
    body === undefined
      ? {}
      : {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-FLG-CSRF': csrf! },
          body: JSON.stringify(body),
        },
  );
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Não foi possível consultar a publicação.');
  return data;
}

export function Publications({ data, refresh }: { data: State; refresh: () => Promise<unknown> }) {
  const query = new URLSearchParams(location.hash.split('?')[1]);
  const initialCampaignId =
    data.campaigns.find((item) => item.id === query.get('campaign'))?.id || '';
  const [links, setLinks] = useState<LinkedMedia[]>([]);
  const [snapshots, setSnapshots] = useState<MediaInsightSnapshot[]>([]);
  const [selectedId, setSelectedId] = useState(query.get('media') || '');
  const [campaignFilter, setCampaignFilter] = useState(query.get('campaign') || '');
  const [contentFilter, setContentFilter] = useState(query.get('content') || '');
  const eligible = data.contents.filter(
    (c) =>
      ['Reel', 'Carrossel', 'Stories', 'Post'].includes(c.format) &&
      c.stage !== 'descartado' &&
      !c.conflict &&
      !c.sourceMissing,
  );
  const [contentId, setContentId] = useState(
    eligible.find((item) => item.id === query.get('content'))?.id || '',
  );
  const content = data.contents.find((c) => c.id === contentId);
  const [campaignId, setCampaignId] = useState(initialCampaignId);
  const [linkMode, setLinkMode] = useState<'new' | 'existing'>(contentId ? 'existing' : 'new');
  const [newTitle, setNewTitle] = useState('');
  const [notice, setNotice] = useState('');
  const [completedCampaignId, setCompletedCampaignId] = useState('');
  const accounts = data.integration.accounts || [];
  const [accountId, setAccountId] = useState(accounts[0]?.id || '');
  const [reviews, setReviews] = useState<PublicationReview[]>([]);
  const [kind, setKind] = useState<'feed' | 'stories'>('feed');
  const [discovered, setDiscovered] = useState<SocialMedia[] | null>(null);
  const [label, setLabel] = useState('');
  const [campaignEvidence, setCampaignEvidence] = useState('');
  const [reference, setReference] = useState('');
  const [candidate, setCandidate] = useState<{
    candidateId: string;
    media: SocialMedia;
    verifiedAt: string;
  }>();
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [showLink, setShowLink] = useState(query.get('add') === '1');
  const contextCampaign = data.campaigns.find(
    (item) => item.id === (completedCampaignId || initialCampaignId),
  );
  const visible = links.filter(
    (m) =>
      (!campaignFilter ||
        (campaignFilter === 'none' ? !m.campaignId : m.campaignId === campaignFilter)) &&
      (!contentFilter || m.contentId === contentFilter),
  );
  const selected = visible.find((m) => m.id === selectedId) || visible[0];
  const history = snapshots
    .filter((s) => s.linkedMediaId === selected?.id)
    .sort((a, b) => b.collectedAt.localeCompare(a.collectedAt));
  const latest = history[0];
  const piece = data.contents.find((c) => c.id === selected?.contentId);
  const campaign = data.campaigns.find((c) => c.id === selected?.campaignId);

  function selectPublication(id: string) {
    setSelectedId(id);
    requestAnimationFrame(() =>
      document
        .getElementById('publication-detail')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    );
  }
  async function load() {
    const result = await request('/api/meta/publications');
    setLinks(
      result.linked.sort((a: LinkedMedia, b: LinkedMedia) =>
        b.media.publishedAt.localeCompare(a.media.publishedAt),
      ),
    );
    setReviews(result.reviews || []);
    setSnapshots(result.snapshots);
    setLoaded(true);
  }
  useEffect(() => {
    if (data.integration.identity) load().catch((e) => setError(e.message));
  }, [data.integration.identity?.id]);
  async function run(label: string, work: () => Promise<void>) {
    if (busy) return;
    setBusy(label);
    setError('');
    try {
      await work();
    } catch (e) {
      setError((e as Error).message);
      await refresh();
    } finally {
      setBusy('');
    }
  }
  async function findPublication(value: string, mediaKind: 'feed' | 'stories' = kind) {
    setCandidate(undefined);
    const found = await request('/api/meta/publications/locate', data.csrf, {
      accountId,
      reference: value,
      kind: mediaKind,
    });
    setCandidate(found);
    setNotice('');
    setLabel('');
    setCampaignEvidence('');
    setNewTitle(
      found.media.caption.trim().split(/\r?\n/)[0].replace(/\s+/g, ' ').slice(0, 150) ||
        `${format(found.media)} · ${accountLabel(found.media)} · ${new Date(found.media.publishedAt).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}`,
    );
    if (content?.format !== format(found.media)) {
      setContentId('');
    }
  }
  function locate(e: FormEvent) {
    e.preventDefault();
    void run('locate', () => findPublication(reference));
  }
  function link() {
    void run('link', async () => {
      const linked = await request('/api/meta/publications/link', data.csrf, {
        candidateId: candidate?.candidateId,
        ...(linkMode === 'new' ? { newContent: { title: newTitle } } : { contentId }),
        campaignId: campaignId || null,
        label,
        campaignEvidence,
      });
      setSelectedId(linked.id);
      setCampaignFilter(campaignId || 'none');
      setContentFilter(contentFilter ? linked.contentId : '');
      setContentId(linked.contentId);
      setCompletedCampaignId(campaignId);
      setNotice(
        campaignId
          ? `Publicação adicionada à campanha ${data.campaigns.find((item) => item.id === campaignId)?.name}.`
          : 'Publicação adicionada.',
      );
      setCandidate(undefined);
      setShowLink(false);
      await load();
      await refresh();
      const nextQuery = new URLSearchParams({ campaign: campaignId || 'none', media: linked.id });
      if (contentFilter) nextQuery.set('content', linked.contentId);
      window.history.replaceState(
        null,
        '',
        `${location.pathname}${location.search}#publications?${nextQuery}`,
      );
    });
  }
  if (!accounts.length)
    return (
      <section className="panel metrics-empty">
        <Video size={34} />
        <h2>Conecte a Meta para conferir suas publicações</h2>
        <p>Os posts serão localizados nas contas autorizadas.</p>
        {contextCampaign && (
          <a href={`#campaigns?id=${encodeURIComponent(contextCampaign.id)}`}>
            Voltar à campanha: {contextCampaign.name}
          </a>
        )}
        <a className="button primary" href="#connections">
          Ir para Conexões
        </a>
      </section>
    );
  return (
    <div className="publication-workspace">
      {contextCampaign && (
        <a className="text-button" href={`#campaigns?id=${encodeURIComponent(contextCampaign.id)}`}>
          <ArrowLeft size={15} />
          {notice ? 'Abrir campanha' : 'Voltar à campanha'}: {contextCampaign.name}
        </a>
      )}
      {notice && (
        <div className="meta-message" role="status">
          <CheckCheck size={17} />
          {notice}
        </div>
      )}
      <div className="publication-toolbar">
        <div>
          <span className="eyebrow">CONTEÚDO → PUBLICAÇÃO → RESULTADO</span>
          <p>Reels, carrosséis, posts e Stories, com resultados por publicação.</p>
        </div>
        {data.permissions.edit && (
          <button
            onClick={() => {
              setShowLink(!showLink);
              setNotice('');
            }}
            disabled={!!busy}
          >
            <Link2 size={16} />
            {showLink ? 'Fechar formulário' : 'Adicionar publicação'}
          </button>
        )}
      </div>
      {data.permissions.edit && (showLink || (loaded && !links.length)) && (
        <section className="panel reel-linker">
          <h2>Localizar uma publicação na Meta</h2>
          <p className="fine-print">
            Cole o link de uma publicação existente. Depois da conferência, crie seu conteúdo no FLG
            ou associe a um conteúdo que você já cadastrou.
          </p>
          <form onSubmit={locate}>
            <label className="field">
              <span>Conta que publicou</span>
              <select
                value={accountId}
                disabled={!!busy}
                onChange={(e) => {
                  setAccountId(e.target.value);
                  setKind('feed');
                  setDiscovered(null);
                  setReference('');
                  setCandidate(undefined);
                }}
              >
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.platform === 'instagram' ? '@' + (a.username || a.name) : a.name} ·{' '}
                    {a.platform === 'facebook' ? 'Facebook' : 'Instagram'}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Tipo de publicação</span>
              <select
                value={kind}
                disabled={!!busy}
                onChange={(e) => {
                  setKind(e.target.value as 'feed' | 'stories');
                  setCandidate(undefined);
                  setDiscovered(null);
                }}
              >
                <option value="feed">Feed e Reels</option>
                {accounts.find((a) => a.id === accountId)?.platform === 'instagram' && (
                  <option value="stories">Stories ativos</option>
                )}
              </select>
            </label>
            <label className="field">
              <span>Link ou ID da publicação</span>
              <input
                value={reference}
                disabled={!!busy}
                maxLength={500}
                required
                placeholder="Link do Instagram, Facebook ou ID"
                onChange={(e) => {
                  setReference(e.target.value);
                  setCandidate(undefined);
                }}
              />
            </label>
            <button className="primary" disabled={!!busy || data.integration.status === 'expired'}>
              <RefreshCw size={16} className={busy === 'locate' ? 'spin' : ''} />
              {busy === 'locate' ? 'Localizando…' : 'Localizar publicação'}
            </button>
          </form>
          <button
            disabled={!!busy || data.integration.status === 'expired'}
            onClick={() =>
              void run('list', async () => {
                setDiscovered(
                  await request('/api/meta/publications/list', data.csrf, { accountId, kind }),
                );
              })
            }
          >
            <RefreshCw size={16} />{' '}
            {busy === 'list' ? 'Lendo publicações…' : 'Buscar publicações da conta'}
          </button>
          {discovered && (
            <div className="publication-discovery">
              <p>
                {discovered.length} publicações retornadas · até 100 mais recentes. Stories ficam
                disponíveis por até 24 horas.
              </p>
              {discovered.map((m) => (
                <div key={m.externalId}>
                  <span>
                    <strong>{format(m)}</strong> · {stamp(m.publishedAt)}
                    <small>{m.caption || `Sem legenda · ID ${m.externalId}`}</small>
                  </span>
                  <button
                    disabled={!!busy}
                    onClick={() => {
                      setReference(m.permalink);
                      void run('locate', () =>
                        findPublication(
                          m.permalink,
                          m.productType === 'STORY' ? 'stories' : 'feed',
                        ),
                      );
                    }}
                  >
                    Conferir
                  </button>
                </div>
              ))}
            </div>
          )}
          {candidate && (
            <div className="reel-candidate">
              <span className="badge publicado">
                <CheckCheck size={14} />
                {format(candidate.media)} conferido na conta autorizada
              </span>
              <p>
                <strong>{accountLabel(candidate.media)}</strong> · publicado em{' '}
                {stamp(candidate.media.publishedAt)} · São Paulo
              </p>
              <p className="reel-caption">{candidate.media.caption}</p>
              <a href={candidate.media.permalink} target="_blank" rel="noreferrer">
                Conferir no {channelLabel(candidate.media)} <ArrowUpRight size={14} />
              </a>
              <div className="reel-link-fields">
                <label className="field">
                  <span>Como organizar esta publicação?</span>
                  <select
                    value={linkMode}
                    disabled={!!busy}
                    onChange={(event) => setLinkMode(event.target.value as 'new' | 'existing')}
                  >
                    <option value="new">Criar conteúdo a partir desta publicação</option>
                    <option value="existing">Vincular a um conteúdo existente</option>
                  </select>
                </label>
                {linkMode === 'new' ? (
                  <label className="field">
                    <span>Título do conteúdo</span>
                    <input
                      value={newTitle}
                      onChange={(event) => setNewTitle(event.target.value)}
                      maxLength={250}
                      disabled={!!busy}
                      required
                    />
                    <small>
                      Título sugerido a partir da publicação; você pode ajustá-lo. Formato e data
                      vêm da conferência da Meta.
                    </small>
                  </label>
                ) : (
                  <>
                    <label className="field">
                      <span>Conteúdo existente</span>
                      <select
                        value={contentId}
                        disabled={!!busy}
                        onChange={(e) => {
                          setContentId(e.target.value);
                          setCampaignEvidence('');
                        }}
                      >
                        <option value="">Selecione o conteúdo desta publicação</option>
                        {eligible
                          .filter((c) => c.format === format(candidate.media))
                          .map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.flgId || 'Pauta'} · {c.title}
                            </option>
                          ))}
                      </select>
                    </label>
                    {!eligible.some((item) => item.format === format(candidate.media)) && (
                      <p>
                        Nenhum conteúdo compatível cadastrado. Escolha criar conteúdo a partir desta
                        publicação.
                      </p>
                    )}
                  </>
                )}
                <label className="field">
                  <span>Campanha desta publicação</span>
                  <select
                    value={campaignId}
                    disabled={!!busy}
                    onChange={(e) => setCampaignId(e.target.value)}
                  >
                    <option value="">Sem campanha vinculada</option>
                    {data.campaigns.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>Identificação da publicação (opcional)</span>
                  <input
                    maxLength={299}
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="Ex.: Tela 1 · Pergunta de fim de mês"
                  />
                </label>
                {linkMode === 'existing' &&
                  campaignId &&
                  contentId &&
                  !content?.campaignIds.includes(campaignId) && (
                    <label className="field campaign-evidence">
                      <span>Evidência do vínculo com a campanha</span>
                      <textarea
                        aria-label="Evidência do vínculo com a campanha"
                        aria-describedby="campaign-evidence-help"
                        value={campaignEvidence}
                        maxLength={2000}
                        onChange={(e) => setCampaignEvidence(e.target.value)}
                        placeholder="Informe a fonte e o trecho que associam esta publicação à campanha."
                      />
                      <small id="campaign-evidence-help">
                        Este vínculo vale só para esta publicação. As demais publicações da peça
                        mantêm suas associações.
                      </small>
                    </label>
                  )}
                <button
                  className="primary"
                  onClick={link}
                  disabled={
                    !!busy ||
                    (linkMode === 'new' ? !newTitle.trim() : !contentId) ||
                    (linkMode === 'existing' &&
                      !!campaignId &&
                      !content?.campaignIds.includes(campaignId) &&
                      campaignEvidence.trim().length < 20)
                  }
                >
                  {busy === 'link'
                    ? 'Adicionando…'
                    : linkMode === 'new'
                      ? 'Criar conteúdo e adicionar publicação'
                      : 'Confirmar vínculo'}
                </button>
              </div>
            </div>
          )}
        </section>
      )}
      {error && (
        <div className="meta-message error" role="alert">
          {error} As leituras já salvas permanecem disponíveis.
        </div>
      )}
      {links.length > 0 && (
        <section className="panel publication-filters">
          <label className="field">
            <span>Campanha</span>
            <select value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)}>
              <option value="">Todas as campanhas</option>
              <option value="none">Sem campanha vinculada</option>
              {data.campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Publicação vinculada</span>
            <select value={selected?.id || ''} onChange={(e) => selectPublication(e.target.value)}>
              {!visible.length && <option value="">Nenhuma publicação neste filtro</option>}
              {visible.map((m) => {
                const c = data.contents.find((c) => c.id === m.contentId);
                return (
                  <option key={m.id} value={m.id}>
                    {c?.flgId || 'Peça'} · {channelLabel(m.media)} · {m.label || format(m.media)} ·{' '}
                    {stamp(m.media.publishedAt)}
                  </option>
                );
              })}
            </select>
          </label>
          {contentFilter && (
            <button onClick={() => setContentFilter('')}>Ver todas as peças</button>
          )}
        </section>
      )}
      {visible.length > 0 && (
        <section className="panel publication-index">
          <div className="publication-index-heading">
            <h2>
              {visible.length}{' '}
              {visible.length === 1 ? 'publicação vinculada' : 'publicações vinculadas'}
            </h2>
            <span>
              {new Set(visible.map((m) => m.contentId)).size}{' '}
              {new Set(visible.map((m) => m.contentId)).size === 1
                ? 'conteúdo no FLG'
                : 'conteúdos no FLG'}
            </span>
          </div>
          <p className="fine-print">
            Última leitura de cada publicação. Alcance e visualizadores únicos não são somados entre
            posts ou contas.
          </p>
          <div className="publication-table-wrap">
            <table className="publication-table">
              <thead>
                <tr>
                  <th>Peça / publicação</th>
                  <th>Conta e data</th>
                  <th>Visualizações</th>
                  <th>Alcance / únicos</th>
                  <th>Leitura</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((m) => {
                  const c = data.contents.find((c) => c.id === m.contentId);
                  const last = snapshots
                    .filter((s) => s.linkedMediaId === m.id)
                    .sort((a, b) => b.collectedAt.localeCompare(a.collectedAt))[0];
                  return (
                    <tr key={m.id} className={m.id === selected?.id ? 'selected' : ''}>
                      <td>
                        <button
                          className="publication-select"
                          onClick={() => selectPublication(m.id)}
                        >
                          <strong>
                            {c?.flgId || 'Peça'} · {m.label || format(m.media)}
                          </strong>
                          <small>{c?.title}</small>
                        </button>
                      </td>
                      <td>
                        {accountLabel(m.media)}
                        <small>
                          {channelLabel(m.media)} · {stamp(m.media.publishedAt)}
                        </small>
                      </td>
                      <td>{metricValue(last, ['views', 'post_media_view'])}</td>
                      <td>{metricValue(last, ['reach', 'post_total_media_view_unique'])}</td>
                      <td>
                        {last ? (
                          <>
                            <span>
                              {last.metrics.filter((v) => v.status === 'available').length}/
                              {last.metrics.length} métricas
                            </span>
                            <small>{stamp(last.collectedAt)}</small>
                          </>
                        ) : (
                          'Aguardando coleta'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
      {reviews.filter((r) => !contentFilter || r.contentId === contentFilter).length > 0 &&
        !campaignFilter && (
          <section className="panel publication-reviews">
            <h2>Pendências da conferência</h2>
            {reviews
              .filter((r) => !contentFilter || r.contentId === contentFilter)
              .map((r) => (
                <div key={r.id}>
                  <strong>
                    {data.contents.find((c) => c.id === r.contentId)?.flgId} · {r.channel} ·{' '}
                    {r.status === 'outside_window'
                      ? 'Fora da janela da API'
                      : r.status === 'multiple_posts'
                        ? 'Mais de um post'
                        : 'Não localizado'}
                  </strong>
                  <p>{r.note}</p>
                  <small>
                    Conferido em {stamp(r.checkedAt)} · {r.sourceRef?.path}
                  </small>
                </div>
              ))}
          </section>
        )}
      {selected && (
        <>
          <section id="publication-detail" className="panel reel-hero">
            <div className="reel-hero-main">
              <span className="badge yellow">
                {piece?.flgId || 'PEÇA'} · {channelLabel(selected.media).toUpperCase()} ·{' '}
                {format(selected.media)}
              </span>
              <h2>{selected.label || piece?.title}</h2>
              <p>{campaign?.name || 'Sem campanha vinculada no acervo'}</p>
              <div className="reel-meta">
                <span>{accountLabel(selected.media)}</span>
                <span>Publicado em {stamp(selected.media.publishedAt)} · São Paulo</span>
              </div>
            </div>
            <div className="reel-hero-actions">
              <button
                className="primary"
                disabled={!!busy || data.integration.status === 'expired'}
                onClick={() =>
                  void run('collect', async () => {
                    const snapshot = await request('/api/meta/publications/insights', data.csrf, {
                      linkedMediaId: selected.id,
                    });
                    setSnapshots((s) => [...s, snapshot]);
                  })
                }
              >
                <RefreshCw size={16} className={busy === 'collect' ? 'spin' : ''} />
                {busy === 'collect' ? 'Consultando…' : 'Atualizar métricas'}
              </button>
              <a href={selected.media.permalink} target="_blank" rel="noreferrer">
                Abrir no {channelLabel(selected.media)} <ArrowUpRight size={14} />
              </a>
            </div>
          </section>
          <div className="reel-window">
            <span className="badge">ACUMULADO ATÉ A COLETA</span>
            <p>
              {latest
                ? `Leitura de ${stamp(latest.collectedAt)} · publicação com ${age(latest.ageSeconds)} · ${latest.apiVersion}`
                : 'Consulte a Meta para obter a primeira leitura desta publicação.'}
            </p>
            {selected.media.productType === 'STORY' ? (
              <small>
                Story temporário: a API limita a descoberta a 24 horas. As leituras salvas
                permanecem no painel; novas consultas podem ficar indisponíveis.
              </small>
            ) : (
              <small>
                Esta leitura não representa uma janela exata de sete dias. Referência para D+7:{' '}
                {stamp(
                  new Date(Date.parse(selected.media.publishedAt) + 7 * 86400000).toISOString(),
                )}
                . Coleta manual.
              </small>
            )}
          </div>
          {latest ? (
            <div className="metric-readout reel-metrics">
              {latest.metrics.map((m) => (
                <article className="panel metric-card" key={m.key}>
                  <span>{m.label}</span>
                  <strong>
                    {m.value === null
                      ? '—'
                      : m.unit === 'milliseconds'
                        ? `${(m.value / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} s`
                        : m.value.toLocaleString('pt-BR')}
                  </strong>
                  <small>
                    {m.status === 'available'
                      ? ['reach', 'post_total_media_view_unique'].includes(m.key)
                        ? 'Pessoas ou contas únicas · Meta'
                        : 'Valor informado pela Meta'
                      : 'Indisponível nesta leitura'}
                  </small>
                  {m.error && <p>{m.error}</p>}
                </article>
              ))}
            </div>
          ) : (
            <section className="panel metrics-empty">
              <Video size={32} />
              <h2>Publicação vinculada. Falta a primeira leitura.</h2>
              <p>Use Atualizar métricas. Valores ausentes não serão apresentados como zero.</p>
            </section>
          )}
          <section className="panel metric-details">
            <h2>Publicação e origem dos dados</h2>
            <p>
              As métricas pertencem a esta mídia, sem multiplicação por colaboradores.
              {selected.media.platform === 'facebook'
                ? ' Os valores do Facebook pertencem ao post desta Página e podem incluir distribuição paga. Cliques não equivalem a leads. Não são somados ao Instagram.'
                : ' No Instagram, visualizações, curtidas, comentários e interações usam as métricas orgânicas da API. A Meta pode levar até 48 horas para atualizar os dados.'}
            </p>
            <details>
              <summary>Legenda publicada e vínculo com o acervo</summary>
              <p className="reel-caption">{selected.media.caption}</p>
              <p>
                ID da mídia: {selected.media.externalId} · conferido em {stamp(selected.verifiedAt)}
              </p>
              <p>
                Peça: {piece?.flgId} · Campanha: {campaign?.name || 'Sem campanha vinculada'}
              </p>
              {selected.campaignEvidence && (
                <p>
                  Evidência da campanha: {selected.campaignEvidence.note} ·{' '}
                  {selected.campaignEvidence.sourceRef?.path}
                </p>
              )}
              {selected.sourceRef && (
                <p>
                  Fonte: {selected.sourceRef.path} · hash {selected.sourceRef.hash.slice(0, 12)}
                </p>
              )}
            </details>
            {latest?.metrics.map((m) => (
              <details key={m.key}>
                <summary>
                  {m.label} · {m.key}
                </summary>
                <p>
                  {m.description ||
                    m.error ||
                    'A Meta não retornou descrição ou valor para esta métrica.'}
                </p>
                {m.unit === 'milliseconds' && (
                  <p>A API fornece milissegundos; a tela converte para segundos.</p>
                )}
              </details>
            ))}
            <details>
              <summary>Histórico de leituras ({history.length})</summary>
              <div className="reel-history">
                {history.map((s) => (
                  <p key={s.id}>
                    {stamp(s.collectedAt)} · idade {age(s.ageSeconds)} ·{' '}
                    {s.metrics.filter((m) => m.status === 'available').length}/{s.metrics.length}{' '}
                    métricas disponíveis · acumulado até a coleta
                  </p>
                ))}
              </div>
            </details>
          </section>
        </>
      )}
      {loaded && links.length > 0 && !selected && (
        <section className="panel metrics-empty">
          <h2>Nenhuma publicação neste filtro</h2>
          <p>Os demais vínculos permanecem preservados.</p>
        </section>
      )}
    </div>
  );
}
